import { styled, Typography } from '@mui/material';

import { CheckIcon } from '../../../icons';
import { Tooltip } from '../../atoms/Tooltip';
import { type ColorSwatchPickerProps, useColorSwatchPicker } from './useColorSwatchPicker';

type Props = ColorSwatchPickerProps;

/** Touch target of a swatch, larger than the visible disc. */
const SWATCH_TARGET_SIZE = 44;
const SWATCH_DISC_SIZE = 32;

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const Swatches = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
});

const SwatchButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: SWATCH_TARGET_SIZE,
  height: SWATCH_TARGET_SIZE,
  padding: 0,
  border: 0,
  borderRadius: '50%',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
  '&:disabled': {
    cursor: 'default',
    opacity: theme.palette.action.disabledOpacity,
  },
}));

type DiscOwnerState = {
  color: string | null;
  selected: boolean;
};

const Disc = styled('span')<{ ownerState: DiscOwnerState }>(({ theme, ownerState }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: SWATCH_DISC_SIZE,
  height: SWATCH_DISC_SIZE,
  borderRadius: '50%',
  backgroundColor: ownerState.color ?? theme.palette.background.paper,
  color: ownerState.color ? theme.palette.getContrastText(ownerState.color) : theme.palette.text.primary,
  border: ownerState.color
    ? `1px solid ${theme.palette.divider}`
    : `1px dashed ${theme.palette.text.secondary}`,
  boxShadow: ownerState.selected
    ? `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.text.primary}`
    : 'none',
  transition: theme.transitions.create('box-shadow', {
    duration: theme.transitions.duration.shortest,
  }),
}));

/**
 * Single choice among a few colors (radio group of swatches), e.g. the color of a
 * sub-topic's events in the planning. Arrow keys move and pick, like native radios.
 */
export function ColorSwatchPicker(props: Props) {
  const hook = useColorSwatchPicker(props);

  return (
    <Root data-testid={props['data-testid']}>
      <div>
        <Typography id={hook.labelId} variant="body2Emphasis" component="p">
          {props.label}
        </Typography>
        {props.description ? (
          <Typography id={hook.descriptionId} variant="body2">
            {props.description}
          </Typography>
        ) : null}
      </div>

      <Swatches
        role="radiogroup"
        aria-labelledby={hook.labelId}
        aria-describedby={props.description ? hook.descriptionId : undefined}
      >
        {hook.items.map((item, index) => (
          <Tooltip key={item.key} title={item.label}>
            <SwatchButton
              ref={hook.setButtonRef(index)}
              type="button"
              role="radio"
              aria-checked={item.selected}
              aria-label={item.label}
              tabIndex={item.tabIndex}
              disabled={props.disabled}
              onClick={() => hook.handleSelect(item.value)}
              onKeyDown={(event) => hook.handleKeyDown(index, event)}
            >
              <Disc ownerState={{ color: item.value, selected: item.selected }}>
                {item.selected ? <CheckIcon fontSize="small" color="inherit" /> : null}
              </Disc>
            </SwatchButton>
          </Tooltip>
        ))}
      </Swatches>
    </Root>
  );
}

export type { Props as ColorSwatchPickerProps };
export type { ColorSwatchOption } from './useColorSwatchPicker';
